<div class="h-screen relative overflow-hidden flex flex-col justify-between bg-[#f5f5f7] dark:bg-[#050505] text-slate-900 dark:text-gray-100 transition-colors duration-500 text-[14px] leading-normal antialiased selection:bg-indigo-500/30">

  <!-- Navigation -->
  <nav class="fixed top-0 w-full z-50 bg-[#f5f5f7]/90 dark:bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-gray-200 dark:border-white/5 transition-all duration-300">
    <div class="w-full px-6 h-[64px] flex items-center justify-between">
      <div class="flex items-center gap-4 cursor-pointer group" (click)="goBack()">
        <div class="w-8 h-8 rounded-lg bg-slate-900 dark:bg-white text-white dark:text-black flex items-center justify-center shadow-sm hover:scale-105 transition-transform duration-200">
          <i data-lucide="layers" class="w-4 h-4"></i>
        </div>
        <div class="flex items-center gap-3">
          <span class="text-gray-300 dark:text-gray-700 font-light text-xl">/</span>
          <span class="font-sans font-medium text-[15px] tracking-tight text-slate-900 dark:text-white">Home</span>
        </div>
      </div>
      
      <div class="flex items-center gap-6">
        <!-- Theme Toggles -->
        <div class="flex items-center p-1 rounded-full bg-gray-100/80 dark:bg-white/5 border border-gray-200/50 dark:border-white/5 backdrop-blur-md">
          <button (click)="setTheme('light')" [ngClass]="{'text-indigo-600 bg-white shadow-sm': currentTheme === 'light'}" class="p-1.5 rounded-full text-gray-400 hover:text-gray-900 dark:hover:text-white transition-all duration-200 hover:bg-white dark:hover:bg-white/10">
            <i data-lucide="sun" class="w-3.5 h-3.5"></i>
          </button>
          <button (click)="setTheme('dark')" [ngClass]="{'shadow-sm': currentTheme === 'dark'}" class="p-1.5 rounded-full text-gray-400 hover:text-gray-900 dark:hover:text-white transition-all duration-200 hover:bg-white dark:hover:bg-white/10">
            <i data-lucide="moon" class="w-3.5 h-3.5"></i>
          </button>
        </div>

        <div class="hidden md:flex items-center gap-6">
          <a href="#" class="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 font-medium text-[13px] transition-colors">Documentation</a>
        </div>

        <div class="h-4 w-px bg-gray-200 dark:bg-white/10"></div>

        <button class="w-8 h-8 rounded-full bg-transparent border border-gray-200 dark:border-white/10 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white transition-all">
          <i data-lucide="bell" class="w-3.5 h-3.5"></i>
        </button>
      </div>
    </div>
  </nav>

  <!-- Main Content -->
  <main class="relative z-10 pt-[64px] w-full h-full flex flex-col flex-grow overflow-hidden transition-colors duration-500">
    
    <!-- Split Screen Generator Interface -->
    <div class="w-full h-full flex overflow-hidden bg-white dark:bg-[#0a0a0a] fade-in">
      
      <!-- Sidebar -->
      <aside class="flex flex-col border-r border-gray-200 dark:border-white/5 bg-white dark:bg-[#0a0a0a] relative" 
             [style.width.px]="sidebarWidth" 
             [style.minWidth.px]="minSidebarWidth">
        
        <!-- Sidebar Header -->
        <div class="h-14 flex items-center justify-between px-5 border-b border-gray-100 dark:border-white/5 bg-gradient-to-r from-gray-50/50 to-transparent dark:from-white/[0.02] dark:to-transparent">
          <div class="flex items-center gap-2">
            <div class="w-1.5 h-1.5 rounded-full bg-indigo-500"></div>
            <span class="text-[11px] font-bold tracking-widest text-gray-600 dark:text-gray-300 uppercase">Queries</span>
          </div>
          <button class="w-7 h-7 rounded-lg bg-gray-100 dark:bg-white/5 text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-white/10 transition-all flex items-center justify-center">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
          </button>
        </div>

        <!-- Search -->
        <div class="p-3">
          <div class="relative group">
            <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-indigo-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
            </svg>
            <input type="text" [(ngModel)]="searchQuery" placeholder="Filter queries..." class="w-full h-10 pl-10 pr-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200/80 dark:border-white/10 focus:bg-white dark:focus:bg-[#0a0a0a] focus:border-indigo-300 dark:focus:border-indigo-500/50 focus:ring-2 focus:ring-indigo-500/10 text-[13px] transition-all placeholder:text-gray-400 dark:text-white outline-none">
          </div>
        </div>

        <!-- Query List -->
        <div class="flex-1 overflow-y-auto px-2 pb-4 space-y-1">

          <!-- Loading State -->
          <div *ngIf="isLoadingData" class="p-4 text-center">
            <div class="animate-spin w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto mb-2"></div>
            <p class="text-[12px] text-gray-400">Loading questions...</p>
          </div>

          <!-- Task Items -->
          <div *ngFor="let task of filteredTasks" 
               (click)="selectTask(task)" 
               (mouseenter)="showTooltip(task, $event)"
               (mouseleave)="hideTooltip()"
               (mousemove)="updateTooltipPosition($event)"
               class="flex items-start gap-3 p-3 rounded-xl hover:bg-gradient-to-r hover:from-indigo-50/80 hover:to-transparent dark:hover:from-indigo-500/10 dark:hover:to-transparent cursor-pointer transition-all duration-200 group border border-transparent hover:border-indigo-100/50 dark:hover:border-indigo-500/20" 
               [ngClass]="{'bg-gradient-to-r from-indigo-50 to-indigo-50/30 dark:from-indigo-500/15 dark:to-indigo-500/5 border-indigo-200/60 dark:border-indigo-500/30 shadow-sm': task.questionId === selectedQuestionId}">
            <div class="mt-0.5 w-9 h-9 rounded-lg bg-white dark:bg-white/5 border border-gray-200/80 dark:border-white/10 flex items-center justify-center text-gray-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 group-hover:border-indigo-200 dark:group-hover:border-indigo-500/30 transition-all shadow-sm" 
                 [ngClass]="{'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 border-indigo-200 dark:border-indigo-500/30': task.questionId === selectedQuestionId}">
              <svg *ngIf="task.icon === 'camera'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></svg>
              <svg *ngIf="task.icon !== 'camera'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></svg>
            </div>
            <div class="flex-1 min-w-0">
              <p class="font-medium text-[13px] text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white leading-snug mb-1.5 line-clamp-2" 
                 [ngClass]="{'text-gray-900 dark:text-white': task.questionId === selectedQuestionId}">{{ task.name }}</p>
              <div class="flex items-center gap-2">
                <span class="px-2 py-0.5 rounded-md text-[10px] font-mono font-semibold bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-100/50 dark:border-indigo-500/20">ID: {{ task.questionId }}</span>
                <span *ngIf="task.isEdit" class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
              </div>
            </div>
          </div>

        </div>

        <!-- Resize Handle -->
        <div class="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-indigo-500/50 active:bg-indigo-500 transition-colors group z-10"
             (mousedown)="startResize($event)">
          <div class="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-12 -mr-1.5 rounded-full bg-transparent group-hover:bg-indigo-500/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div class="w-0.5 h-6 rounded-full bg-indigo-400/50"></div>
          </div>
        </div>
      </aside>

      <!-- Editor Main Area -->
      <div class="flex-1 flex flex-col min-w-0 bg-transparent">
        
        <!-- Editor Header -->
        <header class="h-auto py-5 px-10 border-b border-gray-100 dark:border-white/5 flex items-start justify-between bg-white dark:bg-[#0a0a0a]">
          <div class="flex flex-col gap-3">
            <div class="flex items-center gap-3">
              <h1 class="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">{{ selectedTask.name }}</h1>
            </div>
            <div class="flex items-center gap-3 text-[13px] text-gray-500 dark:text-gray-400 font-mono">
              <span class="tracking-wide">ID: {{ selectedTask.templateId }}</span>
              <span *ngIf="templateName" class="text-gray-400">•</span>
              <span *ngIf="templateName" class="tracking-wide text-blue-500">{{ templateName }}</span>
            </div>
          </div>
        </header>

        <!-- Canvas Area with Suggestions -->
        <div class="flex-1 overflow-y-auto p-10 bg-transparent flex gap-6">
          <div class="flex-1 max-w-[700px]">
            
            <div class="relative w-full rounded-xl border border-gray-200 dark:border-white/10 shadow-[0_2px_10px_rgba(0,0,0,0.02)] dark:shadow-none bg-white dark:bg-[#111111] p-10">
              
              <label class="block text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-4">Prompt Instruction</label>

              <div class="relative mb-6">
                <textarea [(ngModel)]="selectedTask.instructions" readonly class="w-full h-[200px] bg-transparent resize-none border-none outline-none text-[15px] leading-7 text-gray-800 dark:text-gray-200 placeholder:text-gray-400 dark:placeholder:text-gray-600 font-normal focus:ring-0 p-0 cursor-not-allowed" placeholder="Describe the task instructions here..." spellcheck="false"></textarea>
              </div>

              <!-- Editing/saving prompts has moved to the Creator survey-definition tool -->
              <p class="text-[13px] text-gray-400 dark:text-gray-500 text-right">
                Prompts are now managed in the Creator survey-definition tool.
              </p>

            </div>

          </div>

          </div>

      </div>

    </div>

  </main>

  <!-- Hover Tooltip -->
  <div *ngIf="hoveredTask" 
       class="fixed z-[200] pointer-events-none"
       [style.left.px]="tooltipX"
       [style.top.px]="tooltipY">
    <div class="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-2xl border border-gray-200 dark:border-white/10 p-5 max-w-[380px] min-w-[300px] animate-fade-in">
      <!-- Question Title -->
      <div class="flex items-start gap-3 mb-4">
        <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-100 to-violet-100 dark:from-indigo-500/20 dark:to-violet-500/20 flex items-center justify-center flex-shrink-0">
          <svg class="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
          </svg>
        </div>
        <div class="flex-1 min-w-0">
          <span class="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">Question</span>
          <p class="text-sm font-semibold text-gray-900 dark:text-white leading-snug mt-0.5">{{ hoveredTask.name }}</p>
        </div>
      </div>
      
      <!-- Question ID -->
      <div class="flex items-center gap-2 mb-4">
        <span class="px-2.5 py-1 rounded-lg text-[11px] font-mono font-bold bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-white/10">ID: {{ hoveredTask.questionId }}</span>
        <span *ngIf="hoveredTask.isEdit" class="px-2.5 py-1 rounded-lg text-[10px] font-bold bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20 flex items-center gap-1">
          <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
          Has Prompt
        </span>
      </div>

      <!-- Prompts Section -->
      <div *ngIf="hoveredTask.prompts && hoveredTask.prompts.length > 0">
        <div class="flex items-center gap-2 mb-2">
          <svg class="w-4 h-4 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
          </svg>
          <span class="text-[10px] font-bold text-violet-600 dark:text-violet-400 uppercase tracking-wider">Prompts</span>
        </div>
        <div class="space-y-2 max-h-[180px] overflow-y-auto">
          <div *ngFor="let prompt of hoveredTask.prompts" 
               class="px-3 py-2.5 rounded-xl bg-gradient-to-r from-violet-50/80 to-purple-50/50 dark:from-violet-500/10 dark:to-purple-500/5 border border-violet-100/60 dark:border-violet-500/20">
            <p class="text-[12px] text-violet-700 dark:text-violet-300 leading-relaxed">{{ prompt.text }}</p>
          </div>
        </div>
      </div>
      
      <!-- No Prompts State -->
      <div *ngIf="!hoveredTask.prompts || hoveredTask.prompts.length === 0" class="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10">
        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
        </svg>
        <p class="text-[12px] text-gray-500 dark:text-gray-400 italic">No prompts configured yet</p>
      </div>
    </div>
  </div>

</div>
